var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback } from "react";
import SearchInput from "../search";
import { Icon } from "../icon";
import Flex from "../templates/flex";
import Box from "../templates/box";
import { Text } from "../typography";
import Action from "./action";
var Table = forwardRef(function (_a, ref) {
    var handleSearch = _a.handleSearch, children = _a.children, _b = _a.seachPlaceholder, seachPlaceholder = _b === void 0 ? "search" : _b, Pagination = _a.Pagination, selectedRows = _a.selectedRows, bulkActions = _a.bulkActions, testPrefix = _a.testPrefix, props = __rest(_a, ["handleSearch", "children", "seachPlaceholder", "Pagination", "selectedRows", "bulkActions", "testPrefix"]);
    return (React.createElement(Flex, { width: "100%", height: "100%", column: true },
        React.createElement(Flex, { width: "100%" },
            handleSearch && (React.createElement(Box, { width: { max: 50 } },
                React.createElement(SearchInput, { "data-testid": "table-global-search-filter", onChange: function (e) {
                        e.persist();
                        handleSearch(e.target.value);
                    }, placeholder: seachPlaceholder, iconRight: React.createElement(Icon, { name: "magnify" }) }))),
            React.createElement(Flex, { "data-testid": "bulk-actions", width: "100%", justifyContent: "end", margin: [0, 0, 1, 0] }, bulkActions ? (React.createElement(Flex, { height: 12, alignSelf: "end", gap: 1, ali: true, margin: [0, 0, 1, 0] }, bulkActions.map(function (_a) {
                var id = _a.id, icon = _a.icon, handleAction = _a.handleAction, tooltipText = _a.tooltipText, rest = __rest(_a, ["id", "icon", "handleAction", "tooltipText"]);
                return (React.createElement(Action, __assign({ testPrefix: "-bulk".concat(testPrefix), key: id, id: id, icon: icon, handleAction: function () { return handleAction(selectedRows); }, tooltipText: tooltipText }, rest)));
            }))) : (React.createElement(Box, { "aria-hidden": true, as: "span" })))),
        React.createElement(Box, __assign({ sx: { borderCollapse: "collapse" }, ref: ref, as: "table" }, props), children),
        Pagination));
});
Table.Head = forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ ref: ref, sx: { whiteSpace: "nowrap" }, as: "thead", border: { size: "1px", type: "solid", side: "bottom", color: "borderSecondary" } }, props), children));
});
Table.HeadRow = forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ as: "tr", sx: { textAlign: "left" }, height: 12, ref: ref }, props), children));
});
Table.HeadCell = forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ ref: ref, width: { max: 30 }, as: "th" }, props), children));
});
Table.SortingHeadCell = forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children, onSortClicked = _a.onSortClicked, setSortDirection = _a.setSortDirection, sortDirection = _a.sortDirection, filter = _a.filter, props = __rest(_a, ["children", "onSortClicked", "setSortDirection", "sortDirection", "filter"]);
    var onClick = useCallback(function (e) {
        e.preventDefault();
        onSortClicked === null || onSortClicked === void 0 ? void 0 : onSortClicked(e);
    }, [sortDirection, setSortDirection, onSortClicked]);
    var sortingIcons = { asc: "sorting_asc", desc: "sorting_desc" };
    return (React.createElement(Box, __assign({ as: "th", ref: ref }, props),
        React.createElement(Flex, { column: true, position: "relative", cursor: "pointer", gap: 1 },
            React.createElement(Box, { onClick: onClick, position: "relative" },
                children,
                React.createElement(Box, { position: "absolute", width: 4, as: Icon, name: (_b = sortingIcons[sortDirection]) !== null && _b !== void 0 ? _b : null })),
            filter)));
});
Table.Body = forwardRef(function (_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ ref: ref, as: "tbody" }, props), children));
});
Table.Cell = forwardRef(function (_a, ref) {
    var children = _a.children, onClick = _a.onClick, props = __rest(_a, ["children", "onClick"]);
    var handleClick = function () {
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    return (React.createElement(Box, __assign({ height: 12, as: "td", ref: ref }, props, { onClick: handleClick }),
        React.createElement(Flex, { alignItems: "center", height: "100%" }, children)));
});
Table.Row = forwardRef(function (_a, ref) {
    var children = _a.children, onClick = _a.onClick, props = __rest(_a, ["children", "onClick"]);
    var handleClick = function () {
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    return (React.createElement(Box, __assign({ onClick: handleClick, height: 12, as: "tr", ref: ref }, props), children));
});
export var Pagination = function (_a) {
    var pageIndex = _a.pageIndex, pageCount = _a.pageCount, hasNext = _a.hasNext, hasPrevious = _a.hasPrevious, onNextPage = _a.onNextPage, onPreviousPage = _a.onPreviousPage, pageSize = _a.pageSize;
    return (React.createElement(Flex, null,
        React.createElement(Box, { cursor: "pointer", onClick: hasPrevious && onPreviousPage, as: Icon, name: "chevron_left_small" }),
        pageIndex,
        React.createElement(Box, { cursor: "pointer", onClick: hasNext && onNextPage, as: Icon, name: "chevron_right_small" }),
        React.createElement(Text, null,
            "Page ",
            pageIndex,
            " of ",
            pageCount)));
};
export default Table;
